import { useLocale } from "@jmc/core/src/hooks/useLocale";
import { Banner } from "@jmc/solid-design-system/src/components/atoms/Banner/Banner";
import { SnackBar } from "@jmc/solid-design-system/src/components/atoms/SnackBar/SnackBar";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useLocation } from "@reach/router";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import regexifyString from "regexify-string";
import { useLocalStorage } from "usehooks-ts";

interface PrivacyPolicyNotificationProps {
    currentPage?: string;
    LinkComponent: React.ElementType;
    linkProps?: Record<string, undefined>;
    linkUrl: string;
    wrapperClassName?: string;
    loadPrivacyPolicyUpdate: undefined;
    selectPrivacyPolicyData: (state: undefined) => undefined;
    useJnjBrandingHook: () => { jnjFullBranded: boolean };
    allContentstackLegalDocuments: {
        lastPublishedDate: string;
        nodes: {
            onetrust_privacy_policy_id: string;
            publish_details: {
                locale: string;
            };
        }[];
    };
}

const PrivacyPolicyNotification = ({
    currentPage,
    LinkComponent,
    linkProps = {},
    linkUrl,
    wrapperClassName,
    loadPrivacyPolicyUpdate,
    selectPrivacyPolicyData,
    useJnjBrandingHook,
    allContentstackLegalDocuments,
}: PrivacyPolicyNotificationProps): JSX.Element | null => {
    const [alreadySeenPrivacyPolicy, setAlreadySeenPrivacyPolicy] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [lastVisited, setLastVisited] = useLocalStorage("lastVisited", dayjs("1990-01-01T12:00:00.000Z"));
    const [alt, setAlt] = useState("");
    const dispatch = useDispatch();
    const locale = useLocale();
    const url = useLocation();
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBrandingHook();

    const lastPrivacyPolicyUpdate = useSelector((state) => selectPrivacyPolicyData(state), isEqual);

    const policyId = allContentstackLegalDocuments.nodes.find(
        (node) => node.onetrust_privacy_policy_id && node?.publish_details?.locale === locale,
    );

    const handleClose = (): void => {
        setShowNotification(false);
        setLastVisited(dayjs());
    };

    useEffect(() => {
        if (policyId) {
            dispatch(
                loadPrivacyPolicyUpdate({ policyId: policyId.onetrust_privacy_policy_id }) || {
                    lastPublishedDate: "2024-05-30T08:47:29.717Z",
                },
            );
        }
    }, []);

    useEffect(() => {
        if (typeof document !== "undefined") {
            const cookies = document.cookie;
            setAlreadySeenPrivacyPolicy(cookies.includes("OptanonAlertBoxClosed"));
        }
    }, []);

    useEffect(() => {
        if (url.href !== alt) {
            setShowNotification(false);
            setAlt(url.href);
        } else if (
            alreadySeenPrivacyPolicy &&
            lastPrivacyPolicyUpdate?.lastPublishedDate &&
            dayjs(lastVisited).isBefore(dayjs(lastPrivacyPolicyUpdate?.lastPublishedDate))
        ) {
            setShowNotification(true);
        }
    }, [lastPrivacyPolicyUpdate, lastVisited, alreadySeenPrivacyPolicy, url]);

    const snackBarText = (): (string | JSX.Element)[] => {
        const PrivacyPolicyPlaceholder = "[Privacy Policy]";
        const Hereholder = "[here]";
        const input = t(
            "common:We've updated our {{PRIVACY_POLICY}}. You can read it {{HERE}}. By continuing to browse our platform, you agree to the updates.",
            {
                PRIVACY_POLICY: PrivacyPolicyPlaceholder,
                HERE: Hereholder,
            },
        );

        const decorateLink = (match: string): JSX.Element | string => (
            <LinkComponent
                key={`OneTrust-Privacy-Policy-Link-${match}`}
                url={linkUrl}
                name="Privacy Policy"
                placement={currentPage + (jnjFullBranded ? ":Banner" : ":Snackbar")}
                {...linkProps}
            >
                <Typography variant="span">
                    {match === PrivacyPolicyPlaceholder
                        ? t("Privacy Policy", { ns: "common" })
                        : t("here", { ns: "common" })}
                </Typography>
            </LinkComponent>
        );

        return regexifyString({
            pattern: /\[.*?\]/gim,
            decorator: decorateLink,
            input,
        });
    };

    if (!showNotification) return null;

    const content = jnjFullBranded ? (
        <Banner
            id="PrivacyPolicyUpdate"
            type="warning"
            showIcon
            dismissText={t("Close", { ns: "common" })}
            onDismiss={handleClose}
            text={<>{snackBarText()}</>}
        />
    ) : (
        <SnackBar color="warning" onClose={handleClose} margin contentSpacingWidth>
            <SnackBar.Text text={<>{snackBarText()}</>} />
        </SnackBar>
    );

    return wrapperClassName ? <div className={wrapperClassName}>{content}</div> : content;
};

export default PrivacyPolicyNotification;
